import React, { Fragment, useEffect, useMemo, useState } from "react";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Dialog, Transition } from "@headlessui/react"; // ✅ Menu eliminado
import { OFICINAS_POR_MUNICIPIO } from "../catalogos/oficinasPorMunicipio";
import LoaderPremiumLogo from "../components/LoaderPremiumLogo";
import { exportInventarioPlanoPdf } from "../utils/exportInventarioPlanoPdf";

import axios from "axios";
import { API_BASE } from "../apiConfig";

export default function InventarioView() {
  // LOADING + INVENTARIO
  const [loading, setLoading] = useState(true);
  const [inventario, setInventario] = useState([]);

  // FILTROS
  const [estadoSeleccionado, setEstadoSeleccionado] = useState("Todos");
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState("Todos");
  const [municipioSeleccionado, setMunicipioSeleccionado] = useState("Todos");
  const [oficinaSeleccionada, setOficinaSeleccionada] = useState("Todos");
  const [moduloSeleccionado, setModuloSeleccionado] = useState("Todos");
  const [busqueda, setBusqueda] = useState("");

  // EDICIÓN (Drawer)
  const [editando, setEditando] = useState(null);
  const [saving, setSaving] = useState(false);

  // DELETE confirm + optimistic
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Toasts
  const [toasts, setToasts] = useState([]);

  const pushToast = (type, title, message) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const toast = { id, type, title, message };
    setToasts((prev) => [toast, ...prev].slice(0, 4));
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  };

  // ============================
  // OPCIONES SELECT (useMemo SIEMPRE ARRIBA DEL RETURN)
  // ============================
  const estados = useMemo(
    () => ["Todos", ...new Set(inventario.map((i) => i.estadoEquipo))],
    [inventario]
  );

  const proyectos = useMemo(
    () => ["Todos", ...new Set(inventario.map((i) => i.proyecto))],
    [inventario]
  );

  const municipios = useMemo(() => ["Todos", ...Object.keys(OFICINAS_POR_MUNICIPIO)], []);

  const oficinas = useMemo(() => {
    return municipioSeleccionado !== "Todos"
      ? ["Todos", ...(OFICINAS_POR_MUNICIPIO[municipioSeleccionado] || [])]
      : ["Todos", ...new Set(inventario.map((i) => i.oficina))];
  }, [municipioSeleccionado, inventario]);

  const modulos = useMemo(() => {
    return [
      "Todos",
      ...new Set(
        inventario
          .filter((i) => (municipioSeleccionado === "Todos" ? true : i.municipio === municipioSeleccionado))
          .filter((i) => (oficinaSeleccionada === "Todos" ? true : i.oficina === oficinaSeleccionada))
          .map((i) => i.modulo?.toString().trim())
          .filter((m) => m && m !== "0" && m !== "null" && m !== "undefined")
      ),
    ].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [inventario, municipioSeleccionado, oficinaSeleccionada]);

  // ============================
  // FILTRO COMPLETO (useMemo ARRIBA DEL RETURN)
  // ============================
  const inventarioFiltrado = useMemo(() => {
    return inventario.filter((i) => {
      if (estadoSeleccionado !== "Todos" && i.estadoEquipo !== estadoSeleccionado) return false;
      if (proyectoSeleccionado !== "Todos" && i.proyecto !== proyectoSeleccionado) return false;
      if (municipioSeleccionado !== "Todos" && i.municipio !== municipioSeleccionado) return false;
      if (oficinaSeleccionada !== "Todos" && i.oficina !== oficinaSeleccionada) return false;
      if (moduloSeleccionado !== "Todos" && i.modulo !== moduloSeleccionado) return false;

      if (busqueda.trim()) {
        const texto = busqueda.toLowerCase();
        const datos = Object.values(i).join(" ").toLowerCase();
        if (!datos.includes(texto)) return false;
      }

      return true;
    });
  }, [
    inventario,
    estadoSeleccionado,
    proyectoSeleccionado,
    municipioSeleccionado,
    oficinaSeleccionada,
    moduloSeleccionado,
    busqueda,
  ]);

  // ============================
  // CARGAR INVENTARIO
  // ============================
  const cargarInventario = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/inventario`);
      setInventario(res.data || []);
    } catch (err) {
      console.error("ERROR GET INVENTARIO:", err);
      pushToast("error", "Error", "No se pudo cargar el inventario.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarInventario();
  }, []);

  // ============================
  // UTIL: LIMPIAR PAYLOAD UPDATE (NO TOCA TU UI)
  // ============================
  const limpiarPayloadUpdate = (obj) => {
    const copia = { ...obj };
    delete copia.id;
    delete copia.sk;
    delete copia.proyecto;
    delete copia.createdAt;
    delete copia.updatedAt;
    return copia;
  };

  // ============================
  // GUARDAR EDICIÓN ✅ FIX
  // ============================
  const guardarEdicion = async () => {
    if (!editando) return;
    try {
      setSaving(true);

      const payload = limpiarPayloadUpdate(editando);

      await axios.put(`${API_BASE}/inventario`, {
        proyecto: editando?.proyecto,
        sk: editando?.sk,
        ...payload,
      });

      pushToast("success", "Actualizado", "Equipo actualizado correctamente.");
      setEditando(null);
      cargarInventario();
    } catch (err) {
      console.error("ERROR UPDATE:", err);
      pushToast("error", "Error", "No se pudo actualizar el equipo.");
    } finally {
      setSaving(false);
    }
  };

  // ============================
  // ELIMINAR (CONFIRM + OPTIMISTIC) ✅ FIX
  // ============================
  const pedirEliminar = (item) => {
    setConfirmDelete(item);
  };

  const confirmarEliminar = async () => {
    if (!confirmDelete) return;

    const item = confirmDelete;

    // Optimistic remove
    const backup = inventario;
    setDeleting(true);
    setConfirmDelete(null);
    setInventario((prev) => prev.filter((x) => x.sk !== item.sk));

    try {
      await axios.delete(`${API_BASE}/inventario`, {
        data: { proyecto: item.proyecto, sk: item.sk },
      });

      pushToast("success", "Eliminado", "Equipo eliminado correctamente.");
    } catch (err) {
      console.error("ERROR DELETE:", err);
      // revert
      setInventario(backup);
      pushToast("error", "Error", "No se pudo eliminar el equipo (se revirtió).");
    } finally {
      setDeleting(false);
    }
  };

  // ============================
  // EXPORTAR A EXCEL
  // ============================
  const exportarExcel = (items) => {
    if (!items || items.length === 0) {
      pushToast("info", "Sin datos", "La tabla está vacía.");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(items);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventario");
    XLSX.writeFile(wb, "Inventario_Equipos.xlsx");
  };

  // ============================
  // EXPORTAR PDF
  // ============================
  const exportarPDF = (items) => {
    if (!items || items.length === 0) {
      pushToast("info", "Sin datos", "La tabla está vacía.");
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });

    doc.setFontSize(18);
    doc.text("Inventario de Equipos - IMOVEQROO", 14, 15);

    const columnas = ["ID", "Equipo", "Marca", "Modelo", "Serie", "Estado", "Proyecto", "Municipio", "Oficina", "Módulo", "Notas"];

    const filas = items.map((item) => [
      item.id,
      item.equipo,
      item.marca,
      item.modelo,
      item.serie,
      item.estadoEquipo,
      item.proyecto,
      item.municipio,
      item.oficina,
      item.modulo,
      item.notas,
    ]);

    autoTable(doc, {
      startY: 25,
      head: [columnas],
      body: filas,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      margin: { left: 10, right: 10 },
    });

    doc.save("Inventario_Equipos.pdf");
  };

  // ============================
  // CLASES ESTADO
  // ============================
  const estadoBadgeClasses = (estado) => {
    switch (estado) {
      case "BUENO":
        return "bg-emerald-100 text-emerald-700";
      case "EN REPARACIÓN":
        return "bg-amber-100 text-amber-700";
      case "MAL ESTADO":
      case "NO OPERATIVO":
        return "bg-rose-100 text-rose-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  const toastStyle = (type) => {
    switch (type) {
      case "success":
        return "bg-emerald-600";
      case "error":
        return "bg-rose-600";
      case "info":
      default:
        return "bg-slate-900";
    }
  };

  // ✅ AHORA SÍ: LOADER (DESPUÉS DE TODOS LOS HOOKS)
  if (loading) {
    return <LoaderPremiumLogo />;
  }

  // ============================
  // RENDER
  // ============================
  return (
    <div className="p-6">
      {/* TOASTS */}
      <div className="fixed top-4 right-4 z-[80] w-[340px] space-y-2">
        {toasts.map((t) => (
          <div key={t.id} className={`text-white rounded-2xl shadow-lg p-3 ${toastStyle(t.type)}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-bold">{t.title}</div>
                {t.message ? <div className="text-xs opacity-90 mt-0.5">{t.message}</div> : null}
              </div>
              <button
                className="text-white/90 hover:text-white text-lg leading-none"
                onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* HEADER */}
      <div className="mb-4">
        <h1 className="text-3xl font-bold text-slate-900">Inventario de Equipos por Proyecto</h1>
        <p className="text-slate-500 text-sm mt-1">Panel de control de equipos, módulos y proyectos de IP DEPOT.</p>
      </div>

      {/* FILTROS */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Municipio */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-600">Municipio</label>
            <select
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-blue-500"
              value={municipioSeleccionado}
              onChange={(e) => {
                setMunicipioSeleccionado(e.target.value);
                setOficinaSeleccionada("Todos");
                setModuloSeleccionado("Todos");
              }}
            >
              {municipios.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </div>

          {/* Oficina */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-600">Oficina</label>
            <select
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-blue-500"
              value={oficinaSeleccionada}
              onChange={(e) => {
                setOficinaSeleccionada(e.target.value);
                setModuloSeleccionado("Todos");
              }}
            >
              {oficinas.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </div>

          {/* Estado */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-600">Estado</label>
            <select
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm shadow-sm"
              value={estadoSeleccionado}
              onChange={(e) => setEstadoSeleccionado(e.target.value)}
            >
              {estados.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </div>

          {/* Proyecto */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-600">Proyecto</label>
            <select
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm shadow-sm"
              value={proyectoSeleccionado}
              onChange={(e) => setProyectoSeleccionado(e.target.value)}
            >
              {proyectos.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </div>

          {/* Módulo */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-600">Módulo</label>
            <select
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm shadow-sm"
              value={moduloSeleccionado}
              onChange={(e) => setModuloSeleccionado(e.target.value)}
            >
              {modulos.map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>
          </div>

          {/* Búsqueda */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-600">Búsqueda</label>
            <input
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm shadow-sm"
              placeholder="Buscar cualquier dato..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
        </div>

        {/* BOTONES EXPORTACIÓN */}
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <button
            onClick={() => exportarExcel(inventarioFiltrado)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold shadow-md hover:bg-emerald-700"
          >
             Exportar Excel
          </button>
          <button
            onClick={() => exportarPDF(inventarioFiltrado)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-semibold shadow-md hover:bg-rose-700"
          >
             Exportar PDF
          </button>

          <button
            onClick={() => exportInventarioPlanoPdf(inventarioFiltrado)}
            style={{
              background: "#0B1120",
              color: "#fff",
              border: "none",
              padding: "10px 16px",
              borderRadius: "10px",
              fontWeight: "700",
              cursor: "pointer",
              marginLeft: "8px",
            }}
          >
            Generar Plano Maestro
          </button>

          {(saving || deleting) && (
            <div className="ml-auto text-xs text-slate-500">
              {saving ? "Guardando cambios…" : null}
              {deleting ? "Eliminando…" : null}
            </div>
          )}
        </div>
      </div>

      {/* TABLA */}
      <div className="mt-4 border border-slate-200 rounded-2xl bg-white shadow-md overflow-auto max-h-[65vh] relative">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-slate-900 text-white sticky top-0 z-20">
            <tr>
              {[
                "ID",
                "Equipo",
                "Marca",
                "Modelo",
                "Serie",
                "Estado",
                "Proyecto",
                "Municipio",
                "Oficina",
                "Módulo",
                "Notas",
                "Acciones",
              ].map((th) => (
                <th
                  key={th}
                  className={`px-4 py-3 text-left font-semibold text-xs uppercase tracking-wide ${
                    th === "Acciones" ? "sticky right-0 bg-slate-900 z-30" : ""
                  }`}
                >
                  {th}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {inventarioFiltrado.map((i, index) => (
              <tr
                key={i.sk}
                onClick={(e) => {
                  // ✅ Enterprise: si clickean botones o cualquier elemento dentro del td acciones, NO abre drawer
                  if (e.target.closest("button")) return;
                  if (e.target.closest("[data-actions]")) return;
                  setEditando(i);
                }}
                className={`border-b border-slate-100 hover:bg-slate-50 transition cursor-pointer ${
                  index % 2 === 0 ? "bg-white" : "bg-slate-50/70"
                }`}
              >
                <td className="px-4 py-2">{i.id}</td>
                <td className="px-4 py-2">{i.equipo}</td>
                <td className="px-4 py-2">{i.marca}</td>
                <td className="px-4 py-2">{i.modelo}</td>
                <td className="px-4 py-2">{i.serie}</td>

                <td className="px-4 py-2">
                  <span
                    className={`px-3 py-1 rounded-full text-[11px] font-semibold ${estadoBadgeClasses(
                      i.estadoEquipo
                    )}`}
                  >
                    {i.estadoEquipo}
                  </span>
                </td>

                <td className="px-4 py-2">{i.proyecto}</td>
                <td className="px-4 py-2">{i.municipio}</td>
                <td className="px-4 py-2">{i.oficina}</td>
                <td className="px-4 py-2">{i.modulo}</td>
                <td className="px-4 py-2">{i.notas}</td>

                {/* ✅ ACCIONES sticky derecha (sin ⋮) */}
                <td
                  data-actions
                  className={`px-4 py-2 sticky right-0 z-10 border-l border-slate-200 ${
                    index % 2 === 0 ? "bg-white" : "bg-slate-50/70"
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => setEditando(i)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-800"
                      title="Editar"
                      type="button"
                    >
                      Editar
                    </button>

                    <button
                      onClick={() => pedirEliminar(i)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-50 hover:bg-rose-100 text-rose-700"
                      title="Eliminar"
                      type="button"
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {inventarioFiltrado.length === 0 && (
              <tr>
                <td colSpan={12} className="px-4 py-6 text-center text-slate-500 text-sm">
                  No se encontraron equipos con los filtros seleccionados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* DRAWER EDITAR */}
      <Transition appear show={!!editando} as={Fragment}>
        <Dialog as="div" className="relative z-[70]" onClose={() => setEditando(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-120"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-out duration-200"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in duration-150"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-xl bg-white shadow-2xl">
                    <div className="h-full flex flex-col">
                      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                        <div>
                          <Dialog.Title className="text-lg font-bold text-slate-900">Editar Equipo</Dialog.Title>
                          <div className="text-xs text-slate-500 mt-0.5">
                            Proyecto: <span className="font-semibold">{editando?.proyecto}</span>
                          </div>
                        </div>

                        <button
                          className="h-10 w-10 rounded-xl hover:bg-slate-100 text-slate-700 text-2xl leading-none"
                          onClick={() => setEditando(null)}
                          aria-label="Cerrar"
                          type="button"
                        >
                          ×
                        </button>
                      </div>

                      <div className="px-6 py-4 flex-1 overflow-y-auto">
                        {editando && (
                          <div className="space-y-3">
                            {Object.keys(editando).map((key) =>
                              ["id", "sk", "proyecto", "createdAt", "updatedAt"].includes(key) ? null : (
                                <div key={key} className="flex flex-col gap-1">
                                  <label className="text-xs font-semibold text-slate-600">{key}</label>
                                  <input
                                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm shadow-sm focus:ring-2 focus:ring-blue-500"
                                    value={editando[key] || ""}
                                    onChange={(e) =>
                                      setEditando({
                                        ...editando,
                                        [key]: e.target.value,
                                      })
                                    }
                                  />
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>

                      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
                        <div className="flex gap-3">
                          <button
                            onClick={guardarEdicion}
                            disabled={saving}
                            className={`flex-1 px-4 py-2 rounded-xl text-white font-semibold shadow-md ${
                              saving ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                            }`}
                            type="button"
                          >
                            {saving ? "Guardando…" : "Guardar cambios"}
                          </button>

                          <button
                            onClick={() => setEditando(null)}
                            disabled={saving}
                            className="px-4 py-2 rounded-xl bg-white ring-1 ring-slate-200 text-slate-800 font-semibold hover:bg-slate-50"
                            type="button"
                          >
                            Cancelar
                          </button>
                        </div>

                       
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* CONFIRM DELETE MODAL */}
      <Transition appear show={!!confirmDelete} as={Fragment}>
        <Dialog as="div" className="relative z-[75]" onClose={() => setConfirmDelete(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-120"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-150"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl border border-slate-200">
                  <Dialog.Title className="text-lg font-bold text-slate-900">Confirmar eliminación</Dialog.Title>

                  <div className="text-sm text-slate-600 mt-2">
                    Vas a eliminar el equipo del proyecto{" "}
                    <span className="font-semibold">{confirmDelete?.proyecto}</span>.
                    <div className="text-xs text-slate-500 mt-1">
                      ID: {confirmDelete?.id} · Serie: {confirmDelete?.serie}
                    </div>
                  </div>

                  <div className="mt-5 flex gap-3">
                    <button
                      onClick={confirmarEliminar}
                      disabled={deleting}
                      className={`flex-1 px-4 py-2 rounded-xl text-white font-semibold shadow-md ${
                        deleting ? "bg-rose-400 cursor-not-allowed" : "bg-rose-600 hover:bg-rose-700"
                      }`}
                      type="button"
                    >
                      {deleting ? "Eliminando…" : "Eliminar"}
                    </button>

                    <button
                      onClick={() => setConfirmDelete(null)}
                      disabled={deleting}
                      className="px-4 py-2 rounded-xl bg-white ring-1 ring-slate-200 text-slate-800 font-semibold hover:bg-slate-50"
                      type="button"
                    >
                      Cancelar
                    </button>
                  </div>

                  <div className="mt-3 text-[11px] text-slate-500">Esta acción no se puede deshacer.</div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}